import { asSentence } from "../lib/format";
import { useVocabulary } from "../i18n/vocabulary";
import { toneFor, type StatusDomain } from "../lib/status";
import { cx } from "../lib/cx";

/**
 * §2.4: **status is colour PLUS a word, never colour alone.**
 *
 * Colour a word with Fluent's `SystemFillColorSuccess` / `Caution` / `Critical` /
 * `Neutral` — and always draw the word. Under a contrast theme the colour goes
 * and the word is all that is left.
 *
 * Failure mode: a colour-blind moderator cannot tell an approved merchant from a
 * suspended one, and this console is where a misread status puts a store back on
 * the storefront.
 *
 * The word is translated. §4.3: sentence case in `mn` and `en` alike; a value
 * rendered from an enum gets its first letter capitalised rather than shown raw.
 */
export function StatusWord({ code, domain }: { code: string; domain: StatusDomain }) {
  const vocab = useVocabulary();
  const tone = toneFor(domain, code);
  const word = vocab.status(domain, code);
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 text-sm",
        tone === "success" && "text-success",
        tone === "caution" && "text-caution",
        tone === "critical" && "text-critical",
        tone === "neutral" && "text-muted-foreground",
      )}
    >
      {/* The dot is decoration; the word is the content. */}
      <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />
      {word === code ? asSentence(code) : word}
    </span>
  );
}

